// capability - what a command could do at worst, from its parse.
//
// This exists to license SILENCE. Every other reading in the product answers
// "what did this touch"; none of them can say "this touched nothing else",
// and that is the claim a quiet mark on a lane makes. A reading that says
// "no writes found" because it could not see the writer is the one failure
// this module must never produce, so the shape is inverted from a detector's:
//
//   A detector matches the alarming and stays quiet otherwise. Its miss costs a
//   missed alarm.
//   This clears the provably-inert and escalates otherwise. Its miss costs an
//   unnecessary escalation.
//
// The second error is the affordable one, so the verb list here is a CLOSED
// allowlist of programs that only read, and everything else escalates - not
// because it is dangerous, but because this module does not know. Unknown and
// unsafe are different answers and `escalate` says which.
//
// WHAT THIS DELIBERATELY DOES NOT DO. It does not carry the security catalog's
// target tables (`_SYSTEM_DIRS`, `_HOME_TARGETS`, `_REGISTRY_ROOTS`, the
// destructive verb tables). Those live in `backend/signals-catalog/` where they
// are bounded, tested and maintained, and they answer a different question:
// whether something is alarming. Re-deriving them here would produce a third
// copy that drifts. The division is: signals classify the alarming tier, this
// clears the inert one, and everything between goes to the judge.
import { readShellCommands } from "./shellAst.js";

// Ordered, least to most reaching. `worstReach` takes the maximum, so adding a
// rung in the wrong place silently changes what gets cleared.
export const REACH = Object.freeze(["none", "cwd", "temp", "home", "system", "network"]);
const rank = (reach) => REACH.indexOf(reach);
export const worstReach = (a, b) => (rank(a) >= rank(b) ? a : b);

// Programs that read and do not write, delete, execute a payload or open a
// socket. Closed on purpose: a program missing from here costs an escalation,
// a program wrongly IN here costs a false quiet mark, and only one of those is
// recoverable by the judge downstream.
const READ_ONLY_PROGRAMS = new Set([
  "cat", "head", "tail", "less", "more", "nl", "wc", "file", "stat", "basename", "dirname",
  "realpath", "readlink", "ls", "dir", "tree", "pwd", "du", "df", "which", "type", "command",
  "whoami", "id", "groups", "hostname", "date", "uname", "uptime", "printenv", "locale", "ps",
  "grep", "rg", "ack", "ag", "find", "fd", "diff", "cmp", "comm", "sort", "uniq", "cut", "tr",
  "column", "fold", "fmt", "expand", "join", "paste", "rev", "tac", "xxd", "od", "strings",
  "md5sum", "sha1sum", "sha256sum", "shasum", "cksum",
  "echo", "printf", "true", "false", "test", "seq", "yes", "jq", "yq", "base64",
  // Changing the working directory reaches nothing on its own. It matters here
  // because it opens a large share of real command lines (`cd x && ...`), and
  // the rest of the line still has to clear on its own terms.
  "cd", "pushd", "popd", "dirs",
  "get-content", "get-childitem", "get-location", "get-item", "select-string", "measure-object",
  "get-date", "get-host", "test-path", "select-object", "sort-object", "where-object",
  "format-list", "format-table", "out-string", "write-output", "write-host",
]);

// Flags that turn a reader into a writer or an executor. Found by running the
// allowlist over a real corpus and reading what it had cleared: `find` and
// `sort` were both on the list unconditionally, and `find . -delete` and
// `sort -o out` are neither reads nor hypothetical - they are what these
// programs are routinely used for.
// Getopt-style programs BUNDLE short flags: `sed -ni` is `-n -i`, `sort -ro`
// is `-r -o`. A disqualifier anchored to the first letter after the dash
// missed every bundled spelling and cleared `sed -ni '1,5p'` - an in-place
// write. So for those programs the letter matches anywhere in a single-dash
// token. `find` does not bundle (its options are whole words) and keeps the
// exact form. Over-matching a harmless bundle (`yq -I` is indentation, not
// in-place) costs an escalation, which is the affordable direction here.
const DISQUALIFYING_FLAGS = {
  find: /^-(?:delete|exec|execdir|ok|okdir|fprint|fprintf|fls|fprint0)$/i,
  fd: /^(?:-[A-Za-z]*[xX][A-Za-z]*|--exec(?:-batch)?)$/,
  sed: /^-(?:[a-z]*i|-in-place)/i,
  sort: /^-(?:[a-z]*o|-output)/i,
  tail: /^-(?:[a-z]*f|-follow)/i,
  yq: /^(?:-[a-z]*i[a-z]*|--inplace|--in-place)$/i,
};

// sed runs a program of its own, and that program can write (`w`, `W`,
// `s///w`) and execute (`e`, `s///e`) - no flag on the command line says so.
// So sed clears only when every script it was handed matches the two shapes
// that make up nearly all real usage: an address print/delete and a plain
// substitution or transliteration with inert flags. `-f` hands the script to
// a file this walk cannot read, and everything else - hold-space programs,
// semicolon chains, branches - escalates. Over-escalation is the affordable
// error here; a cleared `sed -n 'w /etc/evil'` is not.
const SED_ADDRESS_SCRIPT = /^[0-9$,~+ ]*[pdq=]?$/;

function sedSubstitutionSafe(script) {
  if (!/^[sy]/.test(script)) return false;
  const delimiter = script[1];
  if (!delimiter || /[\\\s\w]/.test(delimiter)) return false;
  // Unpacked rather than matched with a regex so an escaped delimiter cannot
  // smuggle a fourth field past the shape check.
  const fields = [];
  let current = "";
  for (let i = 2; i < script.length; i += 1) {
    const ch = script[i];
    if (ch === "\\") { current += ch + (script[i + 1] ?? ""); i += 1; continue; }
    if (ch === delimiter) { fields.push(current); current = ""; continue; }
    current += ch;
  }
  fields.push(current);
  // pattern, replacement, then flags that neither write (`w`) nor execute (`e`).
  return fields.length === 3 && /^[gIimp0-9]*$/.test(fields[2]);
}

function sedReadsOnly(command) {
  const scripts = [];
  let carried = false;
  for (let i = 0; i < command.args.length; i += 1) {
    const a = command.args[i];
    if (a.kind !== "flag") continue;
    if (/^-[A-Za-z]*f/.test(a.text) || /^--file/.test(a.text)) return false;
    const glued = /^--expression=(.*)$/.exec(a.text);
    if (glued) { scripts.push(glued[1]); carried = true; continue; }
    if (/^-[A-Za-z]*e$/.test(a.text) || a.text === "--expression") {
      const next = command.args[i + 1];
      if (!next || next.kind !== "value" || !next.resolved) return false;
      scripts.push(next.value);
      carried = true;
    }
  }
  if (!carried) {
    const first = command.args.find((a) => a.kind === "value");
    if (!first) return true;
    if (!first.resolved) return false;
    scripts.push(first.value);
  }
  return scripts.every((s) => SED_ADDRESS_SCRIPT.test(s) || sedSubstitutionSafe(s));
}

// Read-only only in some spellings. The flag decides, so the program alone
// cannot clear: `git status` reads, `git push` does not.
const SUBCOMMAND_READERS = {
  git: new Set([
    "status", "log", "diff", "show", "blame", "branch", "remote", "config", "rev-parse",
    "ls-files", "ls-tree", "cat-file", "merge-base", "rev-list", "for-each-ref", "symbolic-ref",
    "check-ignore", "count-objects", "describe", "shortlog", "reflog", "grep", "whatchanged",
  ]),
  npm: new Set(["ls", "view", "info", "outdated", "why"]),
  cargo: new Set(["tree", "metadata"]),
  docker: new Set(["ps", "images", "inspect", "logs"]),
  kubectl: new Set(["get", "describe", "logs"]),
};
// `git config --get` reads; `git config key value` writes. A subcommand that
// reads only in one direction is not cleared without the read flag.
const SUBCOMMAND_READ_FLAGS = { config: /^--(?:get|get-all|list|get-regexp)$/ };

// Subcommands that read in their bare form and write with the right extra
// word or flag: `git branch` lists, `git branch x` creates, `git branch -D x`
// deletes. The validator sees the args after the subcommand and answers
// whether THIS spelling still only reads; a shape it does not recognise
// escalates, which is the affordable direction.
const SUBCOMMAND_READ_SHAPES = {
  "git branch": (rest) => rest.every((a) => a.kind !== "value")
    && !rest.some((a) => /^-(?:[dDmMcCfu]$|-(?:delete|move|copy|force|track|set-upstream|unset-upstream|edit-description|create-reflog))/.test(a.text)),
  "git remote": (rest) => {
    const verb = rest.find((a) => a.kind === "value");
    return !verb || ["show", "get-url"].includes(verb.value.toLowerCase());
  },
  // One positional reads the ref; a second one plants it, `-d` deletes it.
  "git symbolic-ref": (rest) => rest.filter((a) => a.kind === "value").length <= 1
    && !rest.some((a) => /^-(?:d|-delete)$/.test(a.text)),
};

// Read-only verbs whose subject lives behind a socket: the registry, the
// cluster API, the container daemon. `inert` is about writes and stays
// theirs to earn; `reach` answers where the read went, and reporting `none`
// for a command that opened a connection is the kind of silence this module
// exists to refuse.
const SUBCOMMAND_BASE_REACH = {
  "npm view": "network",
  "npm info": "network",
  "npm outdated": "network",
  "kubectl get": "network",
  "kubectl describe": "network",
  "kubectl logs": "network",
  "docker ps": "system",
  "docker images": "system",
  "docker inspect": "system",
  "docker logs": "system",
};

// Programs that reach the network whatever else they do.
const NETWORK_PROGRAMS = new Set([
  "curl", "wget", "ssh", "scp", "sftp", "rsync", "nc", "netcat", "ncat", "telnet", "ftp",
  "invoke-webrequest", "invoke-restmethod", "start-bitstransfer",
]);

const TEMP_PREFIXES = ["/tmp/", "/var/tmp/", "/private/tmp/", "/private/var/tmp/"];
const HOME_PREFIXES = ["~", "/home/", "/users/", "$home", "${home}", "%userprofile%", "$env:userprofile"];
const SYSTEM_PREFIXES = [
  "/etc", "/usr", "/bin", "/sbin", "/boot", "/sys", "/proc", "/dev", "/opt", "/srv",
  "/system", "/library", "/applications", "/var/", "c:\\windows", "c:/windows",
  "%systemroot%", "%windir%", "hkey_", "hklm:", "hkcu:",
];

const looksLikePath = (value) => /[\\/]/.test(value) || /^[.~]/.test(value) || /^[a-z]:[\\/]/i.test(value);
const looksLikeUrl = (value) => /^[a-z][a-z0-9+.-]*:\/\//i.test(value);

function normalize(value) {
  return String(value ?? "").trim().replace(/\\/g, "/").toLowerCase();
}

/**
 * How far a path argument reaches, given the working directory if the trace
 * knew one.
 *
 * Without a `cwd` the ladder cannot tell a repo file from any other path under
 * the user's home, so it answers `home` and the command does not clear. That is
 * the conservative direction and it is why callers should pass a cwd when the
 * trace has one - it is the difference between clearing an agent's ordinary
 * file reads and clearing none of them.
 */
export function pathReach(value, cwd = null) {
  const path = normalize(value);
  if (!path) return "none";
  if (looksLikeUrl(value)) return "network";
  const absolute = path.startsWith("/") || /^[a-z]:\//.test(path) || path.startsWith("~") || path.startsWith("$") || path.startsWith("%");
  if (!absolute) {
    // `../..` climbs out of the working directory, so it is not cwd-scoped
    // however it is spelled.
    return path.split("/").includes("..") ? "home" : "cwd";
  }
  const root = cwd ? normalize(cwd).replace(/\/+$/, "") : null;
  if (root && (path === root || path.startsWith(`${root}/`))) return "cwd";
  if (TEMP_PREFIXES.some((p) => path.startsWith(p))) return "temp";
  if (SYSTEM_PREFIXES.some((p) => path.startsWith(p))) return "system";
  if (HOME_PREFIXES.some((p) => path.startsWith(p))) return "home";
  // An absolute path this ladder does not recognise is treated as reaching the
  // system: an unknown root is not evidence of a harmless one.
  return "system";
}

/** Whether a command's program is known to only read. */
export function programReadsOnly(command) {
  const program = command?.program;
  if (!program) return false;
  const disqualifying = DISQUALIFYING_FLAGS[program];
  if (disqualifying && command.args.some((a) => disqualifying.test(a.text))) return false;
  if (program === "sed") return sedReadsOnly(command);
  if (READ_ONLY_PROGRAMS.has(program)) return true;
  const subcommands = SUBCOMMAND_READERS[program];
  if (!subcommands) return false;
  const first = command.args.find((a) => a.kind === "value");
  if (!first || !subcommands.has(first.value.toLowerCase())) return false;
  const readFlag = SUBCOMMAND_READ_FLAGS[first.value.toLowerCase()];
  if (readFlag && !command.args.some((a) => readFlag.test(a.value))) return false;
  const shape = SUBCOMMAND_READ_SHAPES[`${program} ${first.value.toLowerCase()}`];
  if (shape && !shape(command.args.slice(command.args.indexOf(first) + 1))) return false;
  return true;
}

// Programs whose scripts arrive as arguments: awk's pattern-action program,
// sed's edit script. The script is the first positional unless a flag
// carried it - and locating that positional must skip the VALUES of
// value-taking flags, or `awk -v x=1 '/^\/etc/ ...' f` excludes `x=1` and
// walks the real script as a path, which is the very bug this map fixes.
// A `-f` script file is deliberately NOT excluded: the tool reads that file,
// and a read is what the walk should report.
// Each carrier flag has TWO spellings: separate (`-e '/p/d'` - the next
// value arg is the script) and glued (`-e'/p/d'` - the script rides inside
// the flag token, which the walk already skips as a flag). Anchoring the
// separate form with `$` and forgetting the glued one excluded the trailing
// FILE as "the script" on `sed -e'/p/d' f`.
//
// The letter run allowed BEFORE the carrier letter admits only that tool's
// no-argument short flags. A permissive [A-Za-z]* backtracks through flags
// that glue suffixes of their own: sed's `-ifoo` (in-place, backup suffix
// "foo") let the run consume `i` and the suffix's own `f` satisfy the
// carrier class, and a false carrier suppresses the fallback that excludes
// the real script. For awk nothing bundles before -f at all. Lowercase [ef]
// stays deliberate: awk's `-F,` and sed's `-E` are different flags.
const AWK_SCRIPT = {
  valueFlags: /^-(?:v|F)$/,
  fileCarrier: /^-f$|^--file(?:=|$)/,
  scriptValueFlags: null,
  gluedScriptFlags: /^-f.+$|^--file=/,
};
const SCRIPT_FIRST = {
  awk: AWK_SCRIPT,
  gawk: AWK_SCRIPT,
  mawk: AWK_SCRIPT,
  nawk: AWK_SCRIPT,
  sed: {
    valueFlags: null,
    fileCarrier: /^-[nErsuz]*f$|^--file(?:=|$)/,
    scriptValueFlags: /^-[nErsuz]*e$|^--expression$/,
    gluedScriptFlags: /^-[nErsuz]*[ef].+$|^--(?:expression|file)=/,
  },
};

/** The argument indices holding a script, which the reads/reach walk skips. */
function scriptArgIndices(command) {
  const spec = SCRIPT_FIRST[command?.program];
  if (!spec) return null;
  const exclude = new Set();
  let carried = false;
  for (let i = 0; i < command.args.length; i += 1) {
    const a = command.args[i];
    if (a.kind !== "flag") continue;
    if (spec.scriptValueFlags?.test(a.text)) {
      carried = true;
      if (command.args[i + 1]?.kind === "value") exclude.add(i + 1);
    } else if (spec.gluedScriptFlags?.test(a.text) || spec.fileCarrier?.test(a.text)) {
      carried = true;
    }
  }
  if (!carried) {
    for (let i = 0; i < command.args.length; i += 1) {
      const a = command.args[i];
      if (a.kind === "flag") {
        if (spec.valueFlags?.test(a.text)) i += 1;
        continue;
      }
      exclude.add(i);
      break;
    }
  }
  return exclude;
}

function commandCapability(command, cwd) {
  const reads = [];
  const writes = [];
  const network = [];
  let reach = "none";
  let unresolved = !command.resolved;

  // For script programs the script argument is a PROGRAM by the tool's own
  // grammar, not a path - and an awk regex that opens with a slash
  // (`/^ [a-z]+:$/ ...`) read as a path becomes an unknown absolute root,
  // which the ladder honestly calls system reach. That stamped "reaches
  // outside the project" on calls that never left the repo. The exclusion is
  // definitional, not heuristic: it skips exactly the arguments the tool
  // itself would parse as code, wherever a flag or position put them.
  const scriptIndices = scriptArgIndices(command);

  for (let i = 0; i < command.args.length; i += 1) {
    const a = command.args[i];
    if (scriptIndices?.has(i) || a.kind === "flag") continue;
    if (!a.resolved) { unresolved = true; continue; }
    if (looksLikeUrl(a.value)) { network.push(a.value); reach = worstReach(reach, "network"); continue; }
    if (!looksLikePath(a.value)) continue;
    reads.push(a.value);
    reach = worstReach(reach, pathReach(a.value, cwd));
  }
  for (const r of command.redirects) {
    if (!r.resolved) { unresolved = true; continue; }
    if (!r.writes) continue;
    writes.push(r.target);
    reach = worstReach(reach, pathReach(r.target, cwd));
  }
  if (command.program && NETWORK_PROGRAMS.has(command.program)) reach = worstReach(reach, "network");
  const sub = command.args.find((a) => a.kind === "value");
  const baseReach = command.program && sub && sub.resolved
    ? SUBCOMMAND_BASE_REACH[`${command.program} ${sub.value.toLowerCase()}`]
    : null;
  if (baseReach) reach = worstReach(reach, baseReach);

  const readsOnly = programReadsOnly(command);
  return {
    program: command.program,
    depth: command.depth ?? 0,
    via: command.via ?? null,
    reach,
    reads,
    writes,
    network,
    readsOnly,
    unresolved,
    // A command clears only when the program is known read-only, nothing about
    // it was computed, and it wrote nowhere.
    inert: readsOnly && !unresolved && writes.length === 0 && network.length === 0,
  };
}

/**
 * The worst-case capability of one shell source.
 *
 * @param cwd  the working directory the command ran in, when the trace knows
 *   it. Absent, paths under the user's home cannot be told from repo files and
 *   far less clears - see `pathReach`.
 * @returns {{reach, reads, writes, network, resolved, quiet, escalate, commands}|null}
 *   `quiet` is the only field that licenses a silent mark, and it is false
 *   whenever anything at all was not readable. `escalate` names which of them,
 *   so a caller can hand the judge a reason rather than a bare miss.
 */
export async function readCapability(source, { language = "bash", clipped = false, cwd = null } = {}) {
  const reading = await readShellCommands(source, { language, clipped });
  if (!reading) {
    // No parse at all is the strongest reason to escalate, not a quiet default.
    return {
      reach: "system", reads: [], writes: [], network: [], commands: [],
      resolved: false, complete: false, quiet: false, escalate: "unparsed",
    };
  }

  const all = [...reading.commands, ...reading.nested.filter((c) => !c.opaque)];
  const commands = all.map((c) => commandCapability(c, cwd));
  const opaque = reading.nested.filter((c) => c.opaque);

  const reads = [...new Set(commands.flatMap((c) => c.reads))];
  const writes = [...new Set(commands.flatMap((c) => c.writes))];
  const network = [...new Set(commands.flatMap((c) => c.network))];
  let reach = commands.reduce((acc, c) => worstReach(acc, c.reach), "none");
  // A payload in a language this walk does not flatten could do anything; its
  // reach is unknown, which on a worst-case ladder is the top of it.
  if (opaque.length) reach = worstReach(reach, "system");

  const unknown = commands.filter((c) => !c.readsOnly && c.program);
  const unresolved = commands.filter((c) => c.unresolved);
  // Ordered most specific first: an unflattened payload and a clipped source
  // both leave the reading incomplete, and a caller handing the judge a reason
  // wants to know which.
  const escalate = reading.clipped || reading.truncated ? "clipped"
    : reading.hasError ? "parse-error"
    : opaque.length ? `payload:${opaque[0].language}`
    : !reading.complete ? "unfollowed-payload"
    : unresolved.length ? "unresolved-argument"
    // A concrete capability outranks an absence of knowledge: `curl https://x`
    // is more usefully reported as reaching the network than as a program this
    // module has no entry for, even though both are true.
    : writes.length ? "writes"
    : network.length ? "network"
    : unknown.length ? `unknown-program:${unknown[0].program}`
    : null;

  return {
    reach,
    reads,
    writes,
    network,
    commands,
    opaque: opaque.map((c) => ({ language: c.language, via: c.via })),
    resolved: unresolved.length === 0,
    complete: reading.complete,
    // Every condition, not any: the whole value of this field is that a caller
    // can trust it without re-deriving the reasons.
    quiet: reading.complete && commands.length > 0 && commands.every((c) => c.inert),
    escalate,
  };
}
